/**
 * Audio Utilities
 * 音频处理工具函数 - 用于 ElevenLabs WebSocket 通信
 */

/**
 * Base64 字符串转 ArrayBuffer
 * @param base64 - Base64 编码的字符串
 * @returns ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * ArrayBuffer 转 Base64 字符串
 * @param buffer - ArrayBuffer
 * @returns Base64 编码的字符串
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/**
 * PCM16 数据转 Float32 数据
 * 用于音频播放 (WebSocket 接收的是 PCM16，Web Audio API 需要 Float32)
 * @param pcmData - PCM16 格式的 ArrayBuffer
 * @returns Float32Array
 */
export function pcm16ToFloat32(pcmData: ArrayBuffer): Float32Array {
  const int16Array = new Int16Array(pcmData);
  const float32Array = new Float32Array(int16Array.length);
  for (let i = 0; i < int16Array.length; i++) {
    // PCM16 范围是 -32768 到 32767，转换为 -1.0 到 1.0
    float32Array[i] = int16Array[i]! / 32768;
  }
  return float32Array;
}

/**
 * Float32 数据转 PCM16 数据
 * 用于音频录制 (Web Audio API 输出 Float32，WebSocket 需要 PCM16)
 * @param float32Data - Float32Array
 * @returns Int16Array
 */
export function float32ToPcm16(float32Data: Float32Array): Int16Array {
  const pcmData = new Int16Array(float32Data.length);
  for (let i = 0; i < float32Data.length; i++) {
    // 将 -1.0 到 1.0 转换为 -32768 到 32767，并进行裁剪
    const sample = Math.max(-1, Math.min(1, float32Data[i]!));
    pcmData[i] = Math.round(sample * 32767);
  }
  return pcmData;
}

/**
 * 将 Float32 音频数据转换为 Base64 编码的 PCM16
 * 用于发送音频到 WebSocket
 * @param float32Data - Float32Array 音频数据
 * @returns Base64 编码的 PCM16 字符串
 */
export function float32ToBase64Pcm16(float32Data: Float32Array): string {
  const pcmData = float32ToPcm16(float32Data);
  return arrayBufferToBase64(pcmData.buffer as ArrayBuffer);
}

/**
 * 将 Base64 编码的 PCM16 转换为 AudioBuffer
 * 用于播放从 WebSocket 接收的音频
 * @param base64Audio - Base64 编码的 PCM16 音频
 * @param audioContext - AudioContext 实例
 * @param sampleRate - 采样率，默认 16000
 * @returns AudioBuffer
 */
export function base64Pcm16ToAudioBuffer(base64Audio: string, audioContext: AudioContext, sampleRate: number = 16000): AudioBuffer {
  const arrayBuffer = base64ToArrayBuffer(base64Audio);
  const float32Data = pcm16ToFloat32(arrayBuffer);
  const audioBuffer = audioContext.createBuffer(1, float32Data.length, sampleRate);
  audioBuffer.getChannelData(0).set(float32Data);
  return audioBuffer;
}

/**
 * 音频配置常量
 */
export const AUDIO_CONFIG = {
  /** 采样率 */
  SAMPLE_RATE: 16000,
  /** 声道数 */
  CHANNEL_COUNT: 1,
  /** 缓冲区大小 */
  BUFFER_SIZE: 4096,
  /** 启用回声消除 */
  ECHO_CANCELLATION: true,
  /** 启用噪声抑制 */
  NOISE_SUPPRESSION: true,
} as const;
